import { BillingFacturationNetResponse } from "@/Billing/Api/FacturationNet/Dto/Responses/FacNetBillLightResponse";
import { ExternalBillingClaimDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimDto";
import { MyleObjectType } from "@/Common/Models/Enum/MyleObjectType";
import { EnumSentFaxItemType } from "@/Common/Models/Faxing/EnumSentFaxItemType";
import { EnumSentFaxStatus } from "@/Common/Models/Faxing/EnumSentFaxStatus";
import { AttachedObjectResponse } from "@/Common/Models/FileSelection/Responses/AttachedObject";
import { Guid } from "@/Common/Models/Guid";
import { Comment } from "@/Common/Models/Patient/Comment";
import { LabRequestBillCode, LabRequestType } from "@/Common/Models/Patient/LabRequest";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { IPortalShareableItem } from "@/Common/Models/Portal/UI/IPortalShareableItem";
import { BasePortalShareableItem } from "@/Common/Models/ShareItems/BasePortalShareableItem";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { DirectorySelectableEntity } from "@/ContactDirectory/Models/DirectorySelectableEntity";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";
import { MedfarBillLightPreview } from "@/Patient/Api/MedfarBilling/Dto/Responses/FacturationReponse";
import { ConsultationRequest } from "./ConsultationRequest";
import { LabRequest } from "./LabRequest";
import { PrintInfo } from "./PrintInfo";

class NotesInboxItem {
    public EncounterId: string;
    public EncounterDate: Date | null;
    public ContactId: string;
    public PatientName: string;
    public VisitPurpose: string;
    public EmployeeName: string;
    public Status: string;
    public Invoice: string;
    public HealthCardNumber: string;
    public Diagnosis: string;
    public EmployeeIds: string[];
    public LightMedfarBillsList: MedfarBillLightPreview[];
    public FacturationNetBillsList: BillingFacturationNetResponse[];
    public ExternalBillingClaims: ExternalBillingClaimDto[];
    public PatientId: string;
    public IsAccessProhibited: boolean;
}

class ConsultationRequestsInboxItem extends BasePortalShareableItem implements IPortalShareableItem {
    public ConsultationRequestId: Guid;
    public Speciality: string;
    public AttendingDoctorsName: string;
    public Recipient: string;
    public RecipientContactDirectoryEntityId: string;
    public RecipientContactDirectoryEntityType: DirectoryEntityType | null;
    public RecipientDirectorySelectedEntity: DirectorySelectableEntity<IDirectoryEntity> | null;
    public ConsultationRequestDate: Date;
    public Status: string;
    public SiteId: string;
    public Description: string;
    public DoctorLicenseNumber: string;
    public IsSignedOff: boolean;
    public SignedOffDate: Date;
    public SignedOffByEmployeeId: Guid;
    public IsUrgent: boolean;
    public SupervisorContactId: string;
    public IsFaxed: boolean;
    public SiteName: string;
    public SiteStreetAddress: string;
    public SiteZipCode: string;
    public SiteCity: string;
    public ReferralConsultationDate: Date;

    public PatientId: Guid;
    public PatientName: string;
    public PatientNAM: string;
    public PatientNAMExpDate: Date;
    public PatientDOB: Date;
    public PatientFileNumber: string;
    public PatientPhone: string;
    public PatientGender: string;
    public PatientNextAppointmentDateTime: Date;
    public PatientPortalSyncState: number;
    public HasAppointmentToday: boolean;
    public IsPrinted: boolean;
    public EncounterId: Guid;
    public ContactId: string;
    public CostCenterId: string;

    public PatientContactId: Guid;
    public Reason: string;
    public PriorityDropdownId: string;
    public RequestingStaffContactFacetId: Guid;
    public IsSupervisorRequired: boolean;

    public CreatedByContactId: Guid;
    public LastUpdateByContactId: Guid;
    public LastUpdateDate: Date;
    public IsInvalid: boolean;

    public FirstPrintedByEmployeeId: Guid;
    public FirstPrintedDate: Date;

    public StatusLookupId: string;
    public Comments: Comment[];
    public DueDate: Date;
    public StatusChangeDate: Date;
    public IsVisibleInCPP: boolean;
    public SignedPrintAuthorizations: SignedPrintAuthorization[];

    public AttachedDocuments: AttachedObjectResponse[];
    public IsAccessProhibited: boolean;

    constructor(item?: Partial<ConsultationRequestsInboxItem>) {
        super(item);
        Object.assign(this, item);
        this.init();
    }

    public init(): ConsultationRequestsInboxItem {
        SharedItemMessage.initAll(this.SharedItemMessages);
        return this;
    }

    public getMyleObjectType(): MyleObjectType {
        return MyleObjectType.ConsultationRequest;
    }

    public getItemTypeHeader(): string {
        return "";
    }

    public getItemTitle(): string {
        return `${cacheStore.GetString("ConsultationRequestLabel")}: ${this.Speciality}`
            .truncate(SharedItem.SubjectMaxLength);
    }

    public getDefaultSubject(): string {
        return this.Speciality
            ?.truncate(SharedItem.SubjectMaxLength);
    }

    public getPortalObjectId(): string {
        return this.ConsultationRequestId;
    }

    public getPortalLinkedItemType(): PortalLinkedItemType {
        return PortalLinkedItemType.ConsultationRequest;
    }

    public hasBeenModifiedSinceLastShared(): boolean {
        return false;
    }

    public hasBeenSharedOnPreviousEncounter(): boolean {
        return false;
    }

    public static fromConsultationRequest(item: ConsultationRequest, printInfo?: PrintInfo): ConsultationRequestsInboxItem {
        if (!item)
            return null;

        let model = new ConsultationRequestsInboxItem();
        model.ConsultationRequestId = item.id;
        model.RequestingStaffContactFacetId = item.requestingStaffContactFacetId;
        model.IsSupervisorRequired = item.isSupervisorRequired;
        model.PriorityDropdownId = item.priorityDropDownId;
        model.Reason = item.reason;
        model.IsFaxed = item.isFaxed;
        model.IsPrinted = item.isPrinted;
        model.ConsultationRequestDate = item.requestDate;
        model.EncounterId = item.encounterId;
        model.Description = item.description;
        model.Speciality = item.speciality;
        model.Recipient = item.recipient;
        model.RecipientContactDirectoryEntityId = item.recipientContactDirectoryEntityId;
        model.RecipientContactDirectoryEntityType = item.recipientContactDirectoryEntityType;
        model.RecipientDirectorySelectedEntity = item.recipientDirectorySelectedEntity;
        model.LastUpdateByContactId = item.lastUpdatedByContactId;
        model.LastUpdateDate = item.lastUpdateDate;
        model.IsInvalid = item.isInvalid;
        model.FirstPrintedByEmployeeId = item.firstPrintedByEmployeeId;
        model.FirstPrintedDate = item.firstPrintedDate;
        model.StatusLookupId = item.statusLookupId;
        model.Comments = item.comments;
        model.DueDate = item.dueDate;
        model.IsVisibleInCPP = item.isVisibleInCPP;
        model.CreatedByContactId = item.createdByContactId;

        model.SharedItem = item.sharedItem;
        model.SharedItemId = item.sharedItemId;
        model.SharedItemMessages = item.sharedItemMessages;
        model.SignedPrintAuthorizations = item.signedPrintAuthorizations;
        model.AttachedDocuments = item.attachedDocuments;

        if (printInfo) {
            model.SiteId = printInfo.siteId;
            model.SiteName = printInfo.siteName;
            model.SiteStreetAddress = printInfo.siteStreetAddress;
            model.SiteCity = printInfo.siteCity;
            model.SiteZipCode = printInfo.siteZipCode;
            model.PatientId = printInfo.patientId;
            model.PatientName = printInfo.patientName;
            model.PatientNAM = printInfo.patientNAM;
            model.PatientNAMExpDate = printInfo.patientExpiryDate;
            model.PatientGender = printInfo.patientGender;
            model.PatientDOB = printInfo.patientDOB;
            model.PatientFileNumber = printInfo.patientFileNumber;
            model.PatientPhone = printInfo.patientPhoneNumber;
        }
        return model.init();
    }
}

class RequestsInboxItem extends BasePortalShareableItem implements IPortalShareableItem {
    public Reason: string;
    public RequestId: Guid;
    public PatientName: string;
    public Request: string;
    public AttendingDoctorsName: string;
    public RequestDate: Date;
    public Status: string;
    public PatientId: Guid;
    public SiteId: string;
    public DoctorLicenseNumber: string;
    public IsSignedOff: boolean;
    public RISRequestId: string;
    public IsFaxed: boolean;
    public DueDate: Date;
    public StatusChangeDate: Date;
    public CreationDate: Date;

    public IsPrinted: boolean;
    public FirstPrintedByEmployeeId: Guid;
    public FirstPrintedDate: Date;

    public EncounterId: Guid;
    public ContactId: string;
    public Type: LabRequestType;
    public IsSupervisorRequired: boolean;
    public CostCenterId: string;
    public LabRequestBillCodes: LabRequestBillCode[];
    public LastUpdateByContactId: string;
    public CreatedByContactId: string;
    public SignedPrintAuthorizations: SignedPrintAuthorization[];

    public StreetAddress?: string;
    public City?: string;
    public ZipCode?: string;
    public StateLookUpId?: string;
    public CountryLookUpId?: string;
    public PhoneNumber?: string;
    public Extension?: number;
    public PhoneCountryLookUpId?: string;

    public SendStatus?: string;

    public RequestingStaffContactFacetId: Guid;
    public PriorityDropdownId: string;
    public IsInvalid: boolean;

    public StatusLookupId: string;
    public Comments: Comment[];
    public IsVisibleInCPP: boolean;
    public AttachedDocuments: AttachedObjectResponse[];
    public IsAccessProhibited: boolean;
    public Recipient: string | null;
    public RecipientDirectorySelectedEntity: DirectorySelectableEntity<IDirectoryEntity> | null;
    public RecipientContactDirectoryEntityType: DirectoryEntityType | null;
    public RecipientContactDirectoryEntityId: string;

    constructor(item?: RequestsInboxItem) {
        super(item);
        Object.assign(this, item);
        this.init();
    }

    public init(): RequestsInboxItem {
        SharedItemMessage.initAll(this.SharedItemMessages);
        return this;
    }

    public getMyleObjectType(): MyleObjectType {
        return MyleObjectType.ExamRequest;
    }

    public getItemTypeHeader(): string {
        return "";
    }

    public getItemTitle(): string {
        return `${cacheStore.GetString("ExamRequestLabel")}: ${!IsNullOrWhiteSpace(this.Reason) ? this.Reason : this.Request}`
            .truncate(SharedItem.SubjectMaxLength);
    }

    public getDefaultSubject(): string {
        return (IsNullOrWhiteSpace(this.Reason) ? this.Request : this.Reason)
            ?.truncate(SharedItem.SubjectMaxLength);
    }

    public getPortalObjectId(): string {
        return this.RequestId;
    }

    public getPortalLinkedItemType(): PortalLinkedItemType {
        return PortalLinkedItemType.LabRequest;
    }

    public hasBeenModifiedSinceLastShared(): boolean {
        return false;
    }

    public hasBeenSharedOnPreviousEncounter(): boolean {
        return false;
    }

    public static fromLabRequest(item: LabRequest, printInfo?: PrintInfo): RequestsInboxItem {
        if (!item)
            return null;

        let model = new RequestsInboxItem();
        model.RequestId = item.id;
        model.RequestingStaffContactFacetId = item.requestingStaffContactFacetId;
        model.IsSupervisorRequired = item.isSupervisorRequired;
        model.PriorityDropdownId = item.priorityDropDownId;
        model.Reason = item.reason;
        model.IsFaxed = item.isFaxed;
        model.IsPrinted = item.isPrinted;
        model.RequestDate = item.requestDate;
        model.EncounterId = item.encounterId;
        model.Request = item.description;
        model.LastUpdateByContactId = item.lastUpdatedByContactId;
        model.StatusLookupId = item.statusLookupId;
        model.Comments = item.comments;
        model.DueDate = item.dueDate;

        model.LabRequestBillCodes = item.billCodes;
        model.RISRequestId = item.risRequestId;
        model.Type = item.type;
        model.SendStatus = item.sendStatus;
        model.IsInvalid = item.isInvalid;
        model.FirstPrintedByEmployeeId = item.firstPrintedByEmployeeId;
        model.FirstPrintedDate = item.firstPrintedDate;
        model.IsVisibleInCPP = item.isVisibleInCPP;
        model.CreatedByContactId = item.createdByContactId;

        model.SharedItem = item.sharedItem;
        model.SharedItemId = item.sharedItemId;
        model.SharedItemMessages = item.sharedItemMessages;
        model.SignedPrintAuthorizations = item.signedPrintAuthorizations;
        model.AttachedDocuments = item.attachedDocuments;

        model.Recipient = item.recipient;
        model.RecipientContactDirectoryEntityId = item.recipientContactDirectoryEntityId;
        model.RecipientContactDirectoryEntityType = item.recipientContactDirectoryEntityType;

        if (printInfo) {
            model.SiteId = printInfo.siteId;
            model.PatientId = printInfo.patientId;
            model.PatientName = printInfo.patientName;
        }

        return model.init();
    }
}

class APSSFormInboxItem {
    public nom: string;
    public prenom: string;
    public DDN: string;

    public ReqReg_Id: string;
    public ReqReg_EtablissementCreateur: string;
    public nomFormulaireLongue: string;
    public ReqReg_DateCreation: string;
    public ReqReg_DateModification: string;
    public ReqReg_DateChangementEtat: string;
    public changementEtat_Detail: string;
    public saFHIR_infoSupplementaireCRDS: string;
    // heureReTranstransmissionCRDS: string;

    public ReqReg_Etat: string;
    public sousEtat_texte: string;
    public ReqReg_NomCreateur: string;
    public lstPrerequis: string;
    public lstPrerequisManquant: string;
    public nomRef: string;
    public prenomRef: string;
    public NAM: string;
    public texteNumDossierDME: string;
    public dmeReserve_archiveFlag: string;
    public dmeReserve_displayFlag: string;

    public ReqReg_IdType: string;
    public etat_code: string;
    public sousEtat_code: string;
    public etat_texte: string;
}
class ApssPatientLink {
    public T: string;
    public P: Guid;
    public E: string;
}

class SentFaxInboxItem {
    public SentFaxId: number;
    public PatientName: string;
    public ItemTypeName: string;
    public ItemDate: Date;
    public RecipientName: string;
    public RecipientFax: string;
    public DateSent: Date;
    public ReceivedDate: Date;
    public SentBy: string;
    public Status: string;
    public ItemType: EnumSentFaxItemType;
    public ItemId: string;
    public SentFaxFiles: SentFaxFiles[];
    public FailCounterVisibility: boolean;
    public SentStatus: EnumSentFaxStatus;
    public SenderEmployeeId: string;
    public PatientId: string;
    public PatientContactId: string;
    public RequestingStaffContactFacetId: string;
    public ProviderName: string;
    public ProviderError: string;
    public Retries?: number;
    public NextRetryDate?: Date;
    public HasCoverPage?: boolean;

    constructor() {
        this.SentFaxId = null;
        this.PatientName = "";
        this.ItemTypeName = "";
        this.ItemDate = null;
        this.RecipientName = "";
        this.RecipientFax = "";
        this.DateSent = null;
        this.ReceivedDate = null;
        this.SentBy = "";
        this.Status = "";
        this.ItemType = null;
        this.ItemId = "";
        this.SentFaxFiles = [];
        this.FailCounterVisibility = null;
        this.SentStatus = null;
        this.SenderEmployeeId = "";
        this.PatientId = "";
        this.PatientContactId = "";
        this.RequestingStaffContactFacetId = "";
        this.ProviderName = "";
        this.ProviderError = "";
    }

    public get sendFaxTitlePopup(): string {
        switch (this.ItemType) {
            case EnumSentFaxItemType.Prescription:
                return cacheStore.GetString("SignPrescriptionLabel");

            case EnumSentFaxItemType.ConsultationRequest:
                return cacheStore.GetString("SignConsultationRequestLabel");

            case EnumSentFaxItemType.Encounter:
                return cacheStore.GetString("FaxMedicalNoteLabel");

            case EnumSentFaxItemType.LabRequest:
                return cacheStore.GetString("SignLabRequestLabel");

            case EnumSentFaxItemType.Summary:
                return cacheStore.GetString("FaxDocumentsLabel");

            case EnumSentFaxItemType.RadpdfForm:
                return cacheStore.GetString("FormLabel");

            case EnumSentFaxItemType.ReturnedWithComment:
                return cacheStore.GetString("ReturnedWithCommentLabel");

            default:
                return cacheStore.GetString("SendFaxLabel");
        }
    }
}

class SentFaxFiles {
    public FileName: string;
    public FileContent: string;
    public SentFaxId: number;
    public CreationDate: Date;

    constructor() {
        this.FileName = "";
        this.FileContent = "";
        this.SentFaxId = null;
        this.CreationDate = null;
    }
}

class PendingApprovalsItem {
    public PatientFirstName: string;
    public PatientLastName: string;
    public PatientPreferredName: string;
    public ReasonForVisit: string;
    public SupervisorContactId: string;
    public ResidentContactId: string;
    public ConsultationDate: Date;
    public SendDate: Date;
    public Status: number;
    public NumberOfNotes: number;
    public EncounterId: string;
    public PatientContactId: string;
    public Annexes: Annexes[];
    public AppointmentId: string;
    public SiteId: Guid;
    public IsAccessProhibited: boolean;
}

class Annexes {
    public Id: string;
    public Name: string;
    public Type: string;
    public Status: string;
    public ApsshistoricalId: string;

    // SoftVar
    public IsUpdated: boolean;
}

export {
    Annexes,
    APSSFormInboxItem,
    ApssPatientLink,
    ConsultationRequestsInboxItem,
    NotesInboxItem,
    PendingApprovalsItem,
    RequestsInboxItem,
    SentFaxInboxItem
};
